/**
 * THE FLOW — held as data, so every screen can be a pure function of props.
 * ========================================================================
 *
 * One reducer, five screens, no router. Two reasons it is shaped this way rather than as a set of
 * components that each hold their own `useState`:
 *
 *  1. **A kiosk is optional at every step.** The owner requirement is explicit: technicians also
 *     survey *potential* locations where no kiosk is installed, so the entry point must not demand a
 *     KID. That is why {@link KioskContext} has `kioskId: null` as a first-class value and why
 *     {@link NO_KIOSK} exists as a named constant — a screen cannot forget to handle "there is no
 *     kiosk" because there is no shape in which the kiosk is required.
 *  2. **It makes the composition testable without a DOM.** With the state in one reducer, every
 *     screen is a function from props to elements, and the reducer's transitions can be asserted
 *     directly. See `test/ui/`.
 *
 * ⚠️ CORRECTED 2026-09-04 (app-e2e-activation D4) — the flow's POSITION is now in the hash;
 * its STATE still is not, and the original warning is why.
 *
 * The first version of this note said the flow was "deliberately NOT in the URL hash", naming
 * two hazards: two writers on `location.hash`, and *"a back gesture landing on a form with no
 * state"*. Both hazards were real; the conclusion overshot. What it actually rules out is
 * REBUILDING flow state from the hash — and that is still ruled out. What it does not rule out
 * is the reverse direction, which is what exists now:
 *
 *  · the hash REFLECTS the screen (`#/jobs/lookup`, `#/jobs/form`, …) via {@link screenPath},
 *    written only by `WorkFlow` and only while the hash is in the `#/jobs` family — `AppShell`
 *    still owns the top level, so the two writers own disjoint strings;
 *  · a hash POP maps to ONE `{ kind: 'back' }` dispatch — the same transition the on-screen
 *    Back button makes, against in-memory state. The hash is never parsed into a `Screen`;
 *  · a deep link or relaunch into `#/jobs/<anything>` therefore lands on `home` with the hash
 *    corrected via `replaceState` — a fresh process has no flow state, and pretending
 *    otherwise is exactly the named failure mode.
 *
 * Without this, the OS back gesture inside an installed PWA exited the flow (or the app) from
 * any depth, which field-tests as "I lost my place". A half-filled form still does not survive
 * a reload — the *outbox* does, which is the durability promise this app actually makes.
 */

import type { InterventionListItem, KioskPayload } from '@/core/api';

/**
 * What we know about the kiosk this job concerns.
 *
 * `source` is not decoration. "The record says the layout is AAA" and "I typed this KID and the app
 * never reached the server" are different epistemic positions, and the screens say which one they are
 * in rather than presenting both as knowledge.
 */
export interface KioskContext {
  /** `null` means genuinely no kiosk: a site survey, or a location with nothing installed yet. */
  readonly kioskId: string | null;
  /** The reduced C-15 payload, when we have it. `null` whenever we do not, including offline. */
  readonly payload: KioskPayload | null;
  readonly source: 'none' | 'server' | 'session' | 'typed-only';
}

/** No kiosk. A whole, valid starting point — not a degraded one. */
export const NO_KIOSK: KioskContext = { kioskId: null, payload: null, source: 'none' };

/**
 * The job this visit is against, when the technician arrived from the work list.
 *
 * Carried as an OPTIONAL property on the screens rather than as a required one, and that is a
 * deliberate shape rather than laziness: PRD §4.3 demotes manual KID entry to a fallback but does
 * not delete it, and a site survey may have no dispatch at all. `undefined` therefore means "no
 * dispatch", and it makes those two paths type-identical to what they were before the work list
 * existed — so nothing that already worked can be broken by adding it.
 *
 * Its read time travels with it because `InterventionListPage._meta` carries no `retrievedAt`,
 * unlike `KioskPayload._meta` — see `FormHost`'s `interventionFetchedAt`.
 */
export interface JobDispatch {
  readonly intervention: InterventionListItem;
  /** Epoch ms at which the work list that produced this row was read. */
  readonly fetchedAt: number;
}

export type Screen =
  | { readonly name: 'home' }
  | { readonly name: 'lookup' }
  /** The work list: "here are your assigned jobs → pick one → the kiosk comes with it" (PRD §4.3). */
  | { readonly name: 'jobs' }
  | { readonly name: 'job-types'; readonly kiosk: KioskContext; readonly dispatch?: JobDispatch }
  | {
      readonly name: 'form';
      readonly kiosk: KioskContext;
      readonly schemaId: string;
      readonly dispatch?: JobDispatch;
    }
  | {
      readonly name: 'queued';
      readonly kiosk: KioskContext;
      readonly schemaId: string;
      readonly outboxId: string;
      readonly idempotencyKey: string;
      readonly dispatch?: JobDispatch;
    };

export type FlowAction =
  | { readonly kind: 'home' }
  | { readonly kind: 'lookup' }
  | { readonly kind: 'jobs' }
  /**
   * A kiosk (or explicitly none) has been chosen; go on to pick the job type.
   *
   * `dispatch` rides on this action rather than on its own because picking a job from the work list
   * decides the kiosk AND the intervention in one gesture — two actions would allow a state where
   * one arrived and the other did not.
   */
  | { readonly kind: 'kiosk'; readonly kiosk: KioskContext; readonly dispatch?: JobDispatch }
  | { readonly kind: 'schema'; readonly schemaId: string }
  | { readonly kind: 'queued'; readonly outboxId: string; readonly idempotencyKey: string }
  | { readonly kind: 'back' };

export const INITIAL_SCREEN: Screen = { name: 'home' };

/**
 * The transitions.
 *
 * `back` is explicit per screen rather than a history stack: with five screens a stack is more state
 * to get wrong, and the one non-obvious edge — going back from the form returns to the job-type list
 * *with the same kiosk*, not to the lookup — is easier to read stated once here.
 *
 * An action that does not apply to the current screen returns the screen unchanged. It is not an
 * error: a double tap on a slow phone should be a no-op, not a crash.
 */
export function flowReducer(screen: Screen, action: FlowAction): Screen {
  switch (action.kind) {
    case 'home':
      return INITIAL_SCREEN;
    case 'lookup':
      return { name: 'lookup' };
    case 'jobs':
      return { name: 'jobs' };
    case 'kiosk':
      /*
       * `...(x !== undefined ? { dispatch: x } : {})` rather than `dispatch: action.dispatch`,
       * because `exactOptionalPropertyTypes` is on: writing the key with an `undefined` value is a
       * different type from omitting it, and it would also make every existing
       * `toEqual({ name: 'job-types', kiosk })` assertion depend on how the matcher treats an
       * explicit undefined. Omitting keeps the no-dispatch screen byte-identical to what it was.
       */
      return {
        name: 'job-types',
        kiosk: action.kiosk,
        ...(action.dispatch !== undefined ? { dispatch: action.dispatch } : {}),
      };
    case 'schema':
      return screen.name === 'job-types'
        ? {
            name: 'form',
            kiosk: screen.kiosk,
            schemaId: action.schemaId,
            ...(screen.dispatch !== undefined ? { dispatch: screen.dispatch } : {}),
          }
        : screen;
    case 'queued':
      return screen.name === 'form'
        ? {
            name: 'queued',
            kiosk: screen.kiosk,
            schemaId: screen.schemaId,
            outboxId: action.outboxId,
            idempotencyKey: action.idempotencyKey,
            ...(screen.dispatch !== undefined ? { dispatch: screen.dispatch } : {}),
          }
        : screen;
    case 'back':
      switch (screen.name) {
        case 'home':
          return screen;
        case 'lookup':
        case 'jobs':
          return INITIAL_SCREEN;
        case 'job-types':
          // Back goes where you came FROM. A job picked off the work list returns to the work list;
          // a KID typed by hand returns to the lookup; neither returns home over the other's head.
          return screen.dispatch !== undefined
            ? { name: 'jobs' }
            : screen.kiosk.kioskId === null
              ? INITIAL_SCREEN
              : { name: 'lookup' };
        case 'form':
          return {
            name: 'job-types',
            kiosk: screen.kiosk,
            ...(screen.dispatch !== undefined ? { dispatch: screen.dispatch } : {}),
          };
        case 'queued':
          return INITIAL_SCREEN;
      }
  }
}

/**
 * The hash path fragment for a screen — position only, never state.
 *
 * Deliberately NOT invertible: two screens may share a fragment as long as adjacent screens
 * differ (the browser only needs a history ENTRY per step so a pop can mean "back once").
 * Nothing may ever parse one of these back into a `Screen` — see the header correction.
 */
export function screenPath(screen: Screen): string {
  return screen.name === 'home' ? 'jobs' : `jobs/${screen.name}`;
}
